import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import './ForgotPassword.scss';
import { useSearchParams } from 'react-router';
import { requestUserPasswordResetToken, updateUserPassword} from '../../api/forgotPassword';
import { useB2BDomains, useConfig } from '@gplibraries/react/hooks';

import {
  PageTitle,
  DealerMaskMessage,
  AlertBox,
} from '@gplibraries/react/components';

import ForgotPasswordForm from '../../components/ForgotPassword/ForgotPasswordForm';

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const resetToken = params?.get('token')
    ? decodeURIComponent(params.get('token'))
    : null;
  const userId = params?.get('user')

  const { dealerAccessApiHost } = useConfig();

  const [userType, setUserType] = useState('employee');
  const { data: B2BDomains, loading: B2BLoading, error: B2BError } = useB2BDomains();


  // ===============================
  // LOCAL STATE (manual entry)
  // ===============================
  const [email, setEmail] = useState('');
  const [dealerNumber, setDealerNumber] = useState('');
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const canUseResetToken = Boolean(resetToken);


  
  const { mutate: sendResetEmail, isPending: isSendingEmail } = useMutation({
    mutationFn: async ({ user, email }) =>
      requestUserPasswordResetToken(dealerAccessApiHost, user, email),

    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },

    onError: (err) => {
      console.error('[PASSWORD RESET EMAIL ERROR]', err);
      setSubmitted(true);
      setError(null); 
    },
  });



  
  const { mutate: updatePassword } = useMutation({
    mutationFn: async ({ userId, token, newPassword }) =>
      updateUserPassword(dealerAccessApiHost, userId, newPassword, token),

    onError: (err) => {
      console.error('[PASSWORD UPDATE ERROR]', err);
      setError('Something went wrong updating password.');
    },

    onSuccess: () => {
      setError(null);
      setSubmitted(true);
    },
  });

  // ===============================
  // HANDLE MANUAL VALIDATION
  // ===============================
  
  function handleValidateSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitted(false);

    if (!email) {
      setError('Email address is required.');
      return;
    }

    if (userType === 'dealer' && !dealerNumber) {
      setError('Dealer number is required for dealer password reset.');
      return;
    }
    const emailDomain = email.split('@')[1]?.toLowerCase();


    const isB2BDomain = B2BDomains?.some(
      (domain) => domain.toLowerCase() === emailDomain
    );

    if(isB2BDomain) {
      setError("Password changes are not available for this email address. Your Dealer Access account uses your organization's sign-in system, please update your password using your company’s process.");
      setSubmitted(true);
      return;
    }
    const userIdentifier =
        userType === 'dealer' ? dealerNumber : email;

    sendResetEmail({ user: userIdentifier, email });

  }


  // ===============================
  // RENDER
  // ===============================
  return (
    <>
      <PageTitle>Reset Password</PageTitle>

      <div className="flex-col full">
        <DealerMaskMessage />

        <div className="dealer-password-reset-wrapper">

          {/* ===============================
              NO TOKEN → MANUAL INPUT
          =============================== */}
          
          {!canUseResetToken && (
            <>
              <PageTitle>Reset Password</PageTitle>

              {/* ===============================
                  USER TYPE TOGGLE
              =============================== */}
              <div className="reset-toggle">
                <button
                  type="button"
                  className={userType === 'employee' ? 'active' : ''}
                  onClick={() => {
                    setUserType('employee');
                    setDealerNumber('');
                  }}
                >
                  Dealer Employee
                </button>

                <button
                  type="button"
                  className={userType === 'dealer' ? 'active' : ''}
                  onClick={() => setUserType('dealer')}
                >
                  Dealer
                </button>
              </div>

              {/* ===============================
                  INSTRUCTIONS
              =============================== */}
              {userType === 'employee' && (
                <p className="reset-instructions">
                  Enter the email address associated with your account to reset your password.
                </p>
              )}

              {userType === 'dealer' && (
                <AlertBox type="info">
                  <strong>NOTE:</strong> Only the Dealer Access Admin for the dealership can reset this password.
                  If you are the Dealer Access Admin, enter the 6-digit dealer number, and your email address.
                </AlertBox>
              )}

              {/* ===============================
                  FORM
              =============================== */}
              <form className="flex-col gap-md" onSubmit={handleValidateSubmit}>
                  {userType === 'dealer' && (
                      <label>
                          Dealer Number
                          <input
                              type="text"
                              value={dealerNumber}
                              onChange={(e) => setDealerNumber(e.target.value)}
                              placeholder="123456"
                          />
                      </label>
                  )}

                  <label>
                      Email Address
                      <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="example@email.com"
                      />
                  </label>

                  <div className="reset-submit">
                      <button type="submit" disabled={isSendingEmail }>
                        {isSendingEmail ? 'Sending…' : 'Send Email'}
                      </button>
                  </div>
                  {submitted && !error && (
                      <AlertBox type="success">
                          If the provided email is associated with this account, you will receive an email to reset your password shortly.
                      </AlertBox>
                  )}

                  {submitted && error && (
                      <AlertBox type="error">
                          {error}
                      </AlertBox>
                  )}
              </form>
            </>
          )}


          {/* ===============================
              TOKEN PRESENT → PASSWORD FORM
          =============================== */}
          {(canUseResetToken && !submitted) && (
            <>
              {error && (
                <AlertBox type="error">{error}</AlertBox>
              )}

              <ForgotPasswordForm
                userId={userId}
                resetToken={resetToken}
                onSubmit={(payload) => {
                  updatePassword({
                    userId: payload.userId,
                    token: payload.token,
                    newPassword: payload.newPassword,
                  });
                }}
              />
            </>
          )}

          {(canUseResetToken && submitted) && <AlertBox type={'success'}>Password updated successfully.</AlertBox>}
        </div>
      </div>
    </>
  );
}
